import { createContext, useState, useContext, ReactNode } from "react";
import { Role } from "../utils/types";

// Define the shape of the context value
interface AuthContextType {
  loggedIn: boolean;
  setLoggedIn: (value: boolean) => void;
  role: Role;
  setRole: (role: Role) => void;
  id: string;
  setId: (id: string) => void;
  email: string;
  setEmail: (email: string) => void;
  username: string;
  setUsername: (username: string) => void;
  jwt: string;
  setJwt: (jwt: string) => void;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [role, setRole] = useState<Role>(Role.USER);
  const [id, setId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [jwt, setJwt] = useState<string>("");

  return (
    <AuthContext.Provider
      value={{
        loggedIn,
        setLoggedIn,
        role,
        setRole,
        id,
        setId,
        email,
        setEmail,
        username,
        setUsername,
        jwt,
        setJwt
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Export the context itself
export { AuthContext };
